import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import * as React from 'react';
import { GroupServiceGetManyResponse } from '../../services/api';
import { dateFormat, parseTimePeriodDateForUrl } from '../../utils/date';
import { format } from 'date-fns';
import GroupBadges from './GroupBadges';
import { Link } from 'react-router-dom';
import { GroupStatus } from '../../utils/types';
import GroupActionsMenu from './GroupActionsMenu';
import GroupAssignedTo from './GroupAssignedTo';

export interface GroupCardProps {
  group: GroupServiceGetManyResponse;
  onCompletionStatusChange: () => void;
}

export default function GroupCard({ group, onCompletionStatusChange }: GroupCardProps) {

  const cssClass = classNames(
    'relative', 'grid gap-4', 'px-5', 'py-4', 'border border-l-0',
    {
      'bg-grey-1': group.requestStatus === GroupStatus.COMPLETED,
    },
    {
      'bg-white': group.requestStatus !== GroupStatus.COMPLETED,
    }
  )

  const getCardBorderClass = () => {
    switch (group.requestStatus) {
      case GroupStatus.HIGH_RISK:
      case GroupStatus.HIGH_RISK_ALL:
      case GroupStatus.HIGH_RISK_REQUESTED:
        return 'bg-warning-red';
      case GroupStatus.RERATE_ALL:
      case GroupStatus.RERATE_COMPLETED:
      case GroupStatus.RERATE_REQUESTED:
        return 'bg-accent-coral';
      case GroupStatus.TIER2_ALL:
      case GroupStatus.TIER2_APPROVED:
      case GroupStatus.TIER2_COMPLETED:
      case GroupStatus.TIER2_DECLINED:
      case GroupStatus.TIER2_REQUESTED:
      default:
        return 'bg-allstate-blue';
    }
  }

  return (
    <section className={cssClass}>
      <div className={`absolute top-0 bottom-0 left-0 w-1 ${getCardBorderClass()}`}></div>

      <div className="flex gap-4">
        <div className="flex flex-col flex-1 gap-2">
          <h2 className="text-lg font-bold text-allstate-blue">{ group.policyNumber }: { group.groupName }</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 text-2xs text-grey-8">
              <span className="pi pi-user"></span>
              <span>Requested by {group.requestedBy}</span>
            </div>
            {group.requestedBy && group.requestedDate && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 text-2xs text-grey-8">
                  <span className="pi pi-calendar"></span>
                  <span>Last updated: {format(group?.requestedDate, 'MM/dd/yyyy') } &bull; {format(group?.requestedDate, 'H:mm') } by {group?.requestedBy}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link to={`/groups/${parseTimePeriodDateForUrl(group.timePeriodDate!)}/${group.policyNumber}`}>
            <Button rounded severity="secondary" label="view group" />
          </Link>
          <GroupActionsMenu
            assignedTo={group.assignedTo}
            groupId={group.policyNumber!}
            groupName={group.groupName!}
            timePeriodDate={group.timePeriodDate!}
            requestStatus={group.requestStatus as GroupStatus}
            onComplete={() => onCompletionStatusChange()}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2 text-sm ">
          {group.requestedDate && (
            <div><strong>Date requested:</strong> {format(group.requestedDate, dateFormat)}</div>
          )}
          {/* {group.originalEffectiveDate && (
            <div><strong>Effective date:</strong> {format(group.originalEffectiveDate, dateFormat)}</div>
          )} */}
          {group.reissueDate && (
            <div><strong>Reissue:</strong> {format(group.reissueDate, 'MMMM')}</div>
          )}
          <GroupAssignedTo group={group} />
        </div>
        
        <GroupBadges group={group} />
      </div>
    </section>
  )

}

